import React, { useCallback } from 'react';
import { StyleSheet, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import {
  Button,
  theme,
  useNoticeBannersStackContext,
  useResponsive,
} from '@sg/garnish';

import {
  AccountScreenContainer,
  AccountScreenSection,
  AccountScreenTitle,
} from '@order/components';
import { useLocalizationContext } from '@order/Localization';

import {
  DietaryRestrictions,
  useDietaryRestrictionsMachine,
} from '../../components';

export const AccountDietaryRestrictionsScreen = () => {
  const { t } = useLocalizationContext();
  const { minWidth, currentBreakpoint } = useResponsive();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const { isModified, confirmRestrictions, revertRestrictions } =
    useDietaryRestrictionsMachine();

  // ─────────────────────────────────────────────────────────────────

  const containerStyle = [
    styles.container,
    minWidth.isSM && styles.containerSM,
  ];
  const wrapperStyle = minWidth.isSM && styles.wrapper;

  // ─────────────────────────────────────────────────────────────────

  const confirmDietaryRestrictions = useCallback(() => {
    confirmRestrictions();
    addNoticeBanner({
      text: t('dietary-restrictions.successful-change'),
      palette: 'success',
    });
  }, [addNoticeBanner, confirmRestrictions, t]);

  // revert unconfirmed dietary restrictions on screen blur
  useFocusEffect(useCallback(() => revertRestrictions, [revertRestrictions]));

  // ─────────────────────────────────────────────────────────────────

  return (
    <AccountScreenContainer screen="dietary-restrictions">
      <AccountScreenTitle title={t('account.dietary-restrictions.title')} />

      <AccountScreenSection>
        <View style={containerStyle}>
          <View style={wrapperStyle}>
            <DietaryRestrictions
              shouldHideIllustration={currentBreakpoint.isXS}
            />
          </View>

          <View style={styles.ctaWrapper}>
            <Button
              testID="dietary.restrictions.confirm"
              onPress={confirmDietaryRestrictions}
              size="large"
              style={styles.cta}
              focusOutlineColor={theme.colors.GRAY}
              disabled={!isModified}
            >
              {t('general.confirm')}
            </Button>
          </View>
        </View>
      </AccountScreenSection>
    </AccountScreenContainer>
  );
};

// ────────────────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
  },
  containerSM: {
    borderRadius: theme.radius.large,
    padding: theme.spacing['4'],
    borderWidth: 1,
    borderColor: theme.colors.GRAY,
  },
  wrapper: {
    padding: theme.spacing['4'],
  },
  ctaWrapper: {
    paddingTop: theme.spacing['6'],
    paddingBottom: theme.spacing['4'],
  },
  cta: {
    minWidth: 207,
  },
});
